'use client';

import classNames from 'classnames';
import { CSSProperties, useMemo } from 'react';
import { LinkInfo, Size } from '../../types';
import setDenaliAttribute from '../../utils/setDenaliAttribute';
import IconRenderer, { IconDefinition } from '../IconRenderer';
import LinkButtonOrDiv from '../LinkButtonOrDiv';
import styles from './styles/StatusBadge.module.scss';

export type StatusBadgePresetVariant = 'caution' | 'info' | 'inverse' | 'negative' | 'neutral' | 'positive';
export type StatusBadgeCustomVariant = Pick<CSSProperties, 'background' | 'color'>;

export type StatusBadgeProps = {
  className?: string;
  emphasis: 'mid' | 'strong' | 'weak';
  icon?: IconDefinition<'orientation'>;
  linkInfo?: LinkInfo;
  onClick?: () => void;
  size: Size<'sm' | 'md'>;
  text: string;
  title?: string;
  variant: StatusBadgePresetVariant | StatusBadgeCustomVariant;
};

// https://www.figma.com/design/9Y1n3VoMJAcTGPe82bvySj/Denali-Components?node-id=3559-61646&t=8N0pKlYEr2NhrNzg-4
const StatusBadge = ({ className, emphasis, icon, linkInfo, onClick, size, text, title, variant }: StatusBadgeProps) => {
  const iconElement = useMemo(() => {
    if (icon) {
      return <IconRenderer icon={icon} defaults={{ color: 'currentColor', size: 'xs' }} />;
    }
    return null;
  }, [icon]);

  const content = useMemo(
    () => (
      <>
        {iconElement}
        <span className={styles.textContent} title={title}>
          {text}
        </span>
      </>
    ),
    [iconElement, title, text]
  );

  return (
    <LinkButtonOrDiv
      {...setDenaliAttribute('StatusBadge')}
      className={classNames(
        styles.container,
        styles[size],
        typeof variant === 'string' ? styles[variant] : undefined,
        styles[emphasis],
        { [styles.weak]: emphasis === 'weak' },
        className
      )}
      linkInfo={linkInfo}
      onClick={onClick}
      style={
        typeof variant === 'object'
          ? {
              background: emphasis !== 'weak' ? variant.background : undefined,
              color: variant.color
            }
          : undefined
      }
    >
      {content}
    </LinkButtonOrDiv>
  );
};

export default StatusBadge;
